import useQueueStore from "../../stores/queue-store";
import getB from "../bridges/EkaBridge";
import { DocumentData, QuerySnapshot, Unsubscribe } from "firebase/firestore";
import dayjs from "dayjs";
import onAptSnapshot from "./on-apt-snapshot";
import { FirebaseError } from "firebase/app";
import { notification } from "antd";
import { initFirebaseAuth } from "../bridges/methods";
import { track } from "../tracking";

const areSetsEqual = (setA, setB) =>
  setA.size === setB.size && [...setA].every((value) => setB.has(value));

export type AppointmentListenerStatusUpdate = (props: {
  status: AppointmentSubscriberStatus;
  prevStatus: AppointmentSubscriberStatus;
  clinicId: string;
  date: Date;
}) => void;
export class AppointmentListener {
  private subscribers: AppointmentSubscriber[] = [];
  private handleStatusUpdate: AppointmentListenerStatusUpdate;

  constructor({
    clinicId,
    handleStatusUpdate,
    docids,
  }: {
    clinicId: string;
    handleStatusUpdate: AppointmentListenerStatusUpdate;
    docids: string[];
  }) {
    this.handleStatusUpdate = handleStatusUpdate;
    this.initAppointmentListener({ clinicId, docids });
  }

  initAppointmentListener({
    clinicId,
    date = dayjs(useQueueStore.getState().selectedDate).startOf("day").toDate(),
    docids,
  }: {
    clinicId: string;
    date?: Date;
    docids: string[];
  }) {
    this.subscribers.push(
      new AppointmentSubscriber({
        clinicId,
        date,
        onSnapshot: onAptSnapshot,
        onStatusChange: (prevStatus, status) => {
          this.handleStatusUpdate({ prevStatus, status, date, clinicId });
        },
        docids,
      })
    );
  }

  changeDate({ date }: { date: Date }) {
    const selectedDate = dayjs(date).startOf("day");
    const isDayAlreadySelected = !!this.subscribers.find((subscriber) => {
      return selectedDate.isSame(subscriber.date, "day");
    });
    const isTodaySelected = selectedDate.isSame(dayjs(), "day");

    if (isTodaySelected) {
      //unsubscribe everything except today
      const subscribersToDisconnect = this.subscribers.filter(
        (subscriber) => !dayjs(subscriber.date).isSame(dayjs(), "day")
      );

      this.subscribers = this.subscribers.filter((subscriber) =>
        dayjs(subscriber.date).isSame(dayjs(), "day")
      );

      subscribersToDisconnect.forEach((subscriber) => subscriber.disconnect());
      return;
    } else if (isDayAlreadySelected) {
      return;
    }

    const { clinicId, selectedDoctor, isAllDoctorsHidden, getMyDotorIds } =
      useQueueStore.getState();

    const docs = isAllDoctorsHidden() ? [selectedDoctor?.oid] : getMyDotorIds();

    if (docs.filter(Boolean).length === 0) {
      console.warn("No doctors found for clinic", clinicId);
      return;
    }

    this.subscribers.push(
      new AppointmentSubscriber({
        clinicId,
        date,
        onSnapshot: onAptSnapshot,
        onStatusChange: (prevStatus, status) => {
          this.handleStatusUpdate({ clinicId, date, status, prevStatus });
        },
        docids: docs as string[],
      })
    );
  }

  changeClinic({ clinicId }: { clinicId: string }) {
    const { selectedDate, selectedDoctor, isAllDoctorsHidden, getMyDotorIds } =
      useQueueStore.getState();

    const isTodaySelected = dayjs(selectedDate).isSame(dayjs(), "day");

    const docs = isAllDoctorsHidden() ? [selectedDoctor?.oid] : getMyDotorIds();

    if (docs.filter(Boolean).length === 0) {
      console.warn("No doctors found for clinic", clinicId);
      return;
    }

    if (
      this.subscribers.length &&
      this.subscribers[0].date === selectedDate &&
      this.subscribers[0].clinicId === clinicId &&
      areSetsEqual(new Set(this.subscribers?.[0]?.docids), new Set(docs))
    ) {
      // subscribers of this clinic already setup
      return;
    }

    this.subscribers.forEach((subscriber) => subscriber.disconnect());
    this.subscribers = [];

    this.initAppointmentListener({
      clinicId,
      date: selectedDate,
      docids: docs as string[],
    });

    this.subscribers[0].establishNewListener();

    if (!isTodaySelected) {
      this.changeDate({ date: selectedDate });
    }
  }

  changeDoctor({ docids }: { docids: string[] }) {
    console.log("🚀 Firestore", new Date(), "Changing doctor", { docids });
    console.log("🚀 Firestore", new Date(), "Executing Changing doctor", {
      docids,
    });
    const { clinicId, selectedDate } = useQueueStore.getState();

    if (
      this.subscribers.length &&
      this.subscribers[0].clinicId === clinicId &&
      areSetsEqual(new Set(this.subscribers[0].docids), new Set(docids)) &&
      dayjs(this.subscribers[0].date).isSame(selectedDate, "day")
    ) {
      console.log(
        "🚀 Firestore",
        new Date(),
        "Subscribers of this already setup",
        { docids, ...this }
      );
      return;
    }

    this.subscribers.forEach((subscriber) => subscriber.disconnect());
    this.subscribers = [];

    const isTodaySelected = dayjs(selectedDate).isSame(dayjs(), "day");

    this.initAppointmentListener({
      clinicId,
      date: selectedDate,
      docids: docids,
    });

    if (!isTodaySelected) {
      this.changeDate({ date: selectedDate });
    }
  }

  reset() {
    this.subscribers.forEach((subscriber) => subscriber.disconnect());
    this.subscribers = [];
  }

  handlerBrowserBackOnline() {
    this.subscribers.forEach((subscriber) => {
      if (subscriber.status === "DISCONNECTED") {
        subscriber.establishNewListener();
      }
    });
  }

  getAllSubscribers() {
    return this.subscribers;
  }
}

type AppointmentSubscriberStatus = "DISCONNECTED" | "CONNECTED" | "CONNECTING";
class AppointmentSubscriber {
  private canRetryConnection = true;
  clinicId: string;
  date: Date;
  unsubscriber: Promise<Unsubscribe>;
  onSnapshot: (
    Snapshot: QuerySnapshot<DocumentData, DocumentData>
  ) => void | Promise<void>;
  status: AppointmentSubscriberStatus = "CONNECTING";
  onStatusChange: (
    prevStatus: AppointmentSubscriberStatus,
    newStatus: AppointmentSubscriberStatus
  ) => void;
  docids: string[];

  constructor({
    clinicId,
    date,
    onSnapshot,
    onStatusChange,
    docids,
  }: {
    clinicId: string;
    date: Date;
    onSnapshot: (
      Snapshot: QuerySnapshot<DocumentData, DocumentData>
    ) => void | Promise<void>;
    /**
     *
     * @param prevStatus The status which existed previously
     * @param newStatus The new status. Once connection is estabilished, everytime a snapshot is received, a onStatusChange is trigerred with `CONNECTED` as status
     * @returns
     */
    onStatusChange: (
      prevStatus: AppointmentSubscriberStatus,
      newStatus: AppointmentSubscriberStatus
    ) => void;
    docids: string[];
  }) {
    this.clinicId = clinicId;
    this.docids = docids;
    this.date = date;
    this.canRetryConnection = true;
    this.onStatusChange = onStatusChange;
    this.onSnapshot = onSnapshot;
    this.unsubscriber = this.establishNewListener();
  }

  establishNewListener() {
    if (this.unsubscriber) {
      console.log(
        "🚀 Firestore",
        new Date(),
        "Disconnecting listener for clinic",
        { ...this }
      );
      this.unsubscriber.then((unsub) => unsub());
    }
    this.setStatus("CONNECTING");
    console.log(
      "🚀 Firestore",
      new Date(),
      "Establishing new listener for clinic",
      { clinicId: this.clinicId, date: this.date, docids: this.docids }
    );

    const unsubscriber = getB().setupAppointmentListener({
      clinicId: this.clinicId,
      date: this.date,
      onAptSnapshot: async (data) => {
        await this.onSnapshot(data);
        if (!data.metadata.fromCache) {
          this.canRetryConnection = true;
          this.setStatus("CONNECTED");
        }
      },
      onError: (error) => this.onError(error),
      docids: this.docids,
    });

    this.unsubscriber = unsubscriber;
    return unsubscriber;
  }

  disconnect() {
    console.log(
      "🚀 Firestore",
      new Date(),
      "Disconnecting listener for clinic",
      { ...this }
    );
    this.unsubscriber.then((unsub) => unsub());
    this.setStatus("DISCONNECTED");
  }

  private setStatus(status: AppointmentSubscriberStatus) {
    this.onStatusChange(this.status, status);
    this.status = status;
  }

  private async onError(error: FirebaseError) {
    track("FS_ERROR", {
      code: error.code,
      message: error.message,
      can_retry: `${this.canRetryConnection}`,
    });

    if (this.canRetryConnection) {
      this.canRetryConnection = false;
      const isUnauthenticatedError =
        error.code === "unauthenticated" || error.code === "permission-denied";

      if (isUnauthenticatedError) {
        track("FS_TOKEN_REFETCH");
        await initFirebaseAuth();
      }

      this.establishNewListener();
    } else {
      this.setStatus("DISCONNECTED");
      console.error(error);
      track("FS_ERROR_SHOWN", {
        code: error.code,
        message: error.message,
        can_retry: `${this.canRetryConnection}`,
      });
      notification.error({
        message: "Failed to sync appointments",
        description: error.message,
        duration: 5,
      });
    }
  }
}
